import React, {Component, ReactNode} from 'react'
import moment, {Moment} from 'moment'
import EventEmitterExResident from '../../shared/pageBaseComponents/EventEmitterExResident'
import {formatNumberWithCommas} from '../../../formatters/formatNumberWithCommas'
import {
    LeftColumnFlexContainer,
    LeftRowFlexContainer,
    RowFlexContainer,
} from '../../../styledComponents/FlexComponents'
import ReservationCardInformation from '../ReservationCardInformation'
import ReservationsRepository from '../ReservationsRepository'
import {NatAsyncCallFunction} from '../../../__NoAutomatedTests/common/util/Natutil'
import {Nullable} from '../../../app/TypeHelpers'
import NatCurrentDateTimeRepo
    from '../../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {convertToMoment} from '../helpers/helpers'
import {formatDateLocal} from '../../../formatters/formatMoment'
import {ReservationStatusCardInformation} from '../AdditionalChargeCardInformation'
import {PAYMENT_TYPE, RESERVATION_STATUS} from '../../../CodeMaster'
import ReservationCard from '../components/ReservationCard'
import {HKConstants} from '../../../HKConstants'
import styled from 'styled-components'
import {PlainLinkStyledButton} from '../../../styledComponents/Button'
import {backArrow, forwardArrow} from '../../../styledComponents/Links'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {
    BlockSpacer,
    BlockSpacerPcOnly,
    currentMedia,
    HideInPC,
    HideInSP,
} from '../../../styledComponents/ResponsiveConvenience'
import CollapseContainer from '../components/CollapseContainer'
import {CardList, CardWidth} from '../components/CommonStyle'
import Scroll from 'react-scroll'
import Events from 'react-scroll/modules/mixins/scroll-events'
import {NavigateFunction} from 'react-router-dom'

const Element = Scroll.Element
const scroller = Scroll.scroller

export const KEY_SEARCH_DATE = 'ResidentMyReservationsPage.searchDate'
export const KEY_SHOW_CANCELED_RESERVATIONS = 'ResidentMyReservationsPage.showCanceledReservations'

export function setResidentMyReservationsPageSearchDate(date: string | Moment): void {
    let dateString: string
    if (typeof date === 'string') {
        dateString = date
    } else {
        dateString = formatDateLocal(date)
    }
    SessionStorageHelper.setItem(KEY_SEARCH_DATE, dateString)
}

function getResidentMyReservationsPageSearchDate(): Nullable<Moment> {
    const dateString: Nullable<string> = SessionStorageHelper.getStringItemOrNull(KEY_SEARCH_DATE)
    return dateString !== null ? convertToMoment(dateString, null) : null
}

function setResidentMyReservationsPageShowCanceledReservationsState(state: boolean): void {
    SessionStorageHelper.setItem(KEY_SHOW_CANCELED_RESERVATIONS, state.toString())
}

function getResidentMyReservationsPageShowCanceledReservationsState(): Nullable<boolean> {
    const stateString: Nullable<string> =
        SessionStorageHelper.getStringItemOrNull(KEY_SHOW_CANCELED_RESERVATIONS)
    return stateString !== null ? stateString === true.toString() : null
}

export function clearResidentMyReservationsPageDisplayState(): void {
    SessionStorageHelper.removeItem(KEY_SEARCH_DATE)
    SessionStorageHelper.removeItem(KEY_SHOW_CANCELED_RESERVATIONS)
}

interface Props {
    condominiumCd: string
    reservationsRepo: ReservationsRepository
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExResident
    navigate: NavigateFunction
}

interface State {
    reservationCardInformations: ReservationStatusCardInformation[]
    currentTime: Nullable<Moment>
    searchDate: Nullable<Moment>
    showCanceledReservations: boolean
    highlightFutureReservations: boolean
}

class RMRReservationStatusesComponent extends Component<Props, State> {

    scrollTargetName: Nullable<string>

    constructor(props: Readonly<Props>) {
        super(props)

        this.state = {
            reservationCardInformations: [],
            currentTime: null,
            searchDate: null,
            showCanceledReservations: false,
            highlightFutureReservations: false,
        }

        this.scrollTargetName = null

        this.handleChangeShowCanceledReservations = this.handleChangeShowCanceledReservations.bind(this)
        this.handleClickMoveToFutureReservation = this.handleClickMoveToFutureReservation.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {currentDateTimeRepo, eventEmitter, navigate} = this.props

        await eventEmitter.showLoading()
        try {
            const currentTimeResponse = await NatAsyncCallFunction(
                currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
                navigate,
            )
            if (currentTimeResponse === null || currentTimeResponse.details === null) {
                return
            }
            const currentTime: Moment = moment(currentTimeResponse.details.currentDateTime)
            this.setState({currentTime})

            const searchDate: Moment = getResidentMyReservationsPageSearchDate() || currentTime
            await this.getReservationsByMonth(searchDate)

            const showCanceledReservations: boolean =
                getResidentMyReservationsPageShowCanceledReservationsState() === true
            this.setState({showCanceledReservations})

            Events.scrollEvent.register('end', async () => {
                await eventEmitter.waitNextEventLoop(3000)
                this.setState({highlightFutureReservations: false})
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    componentWillUnmount(): void {
        Events.scrollEvent.remove('end')
    }

    async getReservationsByMonth(searchDate: Moment): Promise<void> {
        const {condominiumCd, reservationsRepo, eventEmitter, navigate} = this.props

        await eventEmitter.showLoading()
        try {
            const reservationCardInformations = await NatAsyncCallFunction(
                reservationsRepo.getReservationsByMonth.bind(
                    reservationsRepo,
                    condominiumCd,
                    searchDate,
                ),
                navigate,
            )
            if (reservationCardInformations === null) {
                return
            }
            setResidentMyReservationsPageSearchDate(searchDate)
            this.setState({reservationCardInformations, searchDate})

        } finally {
            eventEmitter.closeLoading()
        }
    }

    async handleClickChangeMonth(offset: number): Promise<void> {
        this.setState({highlightFutureReservations: false})
        const {searchDate} = this.state
        if (searchDate === null) {
            return
        }
        await this.getReservationsByMonth(searchDate.clone().add(offset, 'month'))
    }

    handleClickPrevMonth = () => this.handleClickChangeMonth(-1)
    handleClickNextMonth = () => this.handleClickChangeMonth(1)

    handleChangeShowCanceledReservations(event: React.ChangeEvent<HTMLInputElement>): void {
        const showCanceledReservations = event.target.checked
        setResidentMyReservationsPageShowCanceledReservationsState(showCanceledReservations)
        this.setState({showCanceledReservations})
    }

    handleClickMoveToFutureReservation(): void {
        this.setState({highlightFutureReservations: true}, () => {
            if (this.scrollTargetName !== null) {
                scroller.scrollTo(this.scrollTargetName, {
                    delay: 100,
                    duration: 500,
                    smooth: true,
                })
            }
        })
    }

    handleClick(cardInfo: ReservationStatusCardInformation): void {
        const {condominiumCd, navigate} = this.props
        let url: string
        if (cardInfo instanceof ReservationCardInformation) {
            const {facilityId, facilityReserveId} = cardInfo
            url = `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${facilityReserveId}/detail`
        } else {
            const {facilityReserveId} = cardInfo
            url = `/resident/condominiums/${condominiumCd}/extra-reservations/${facilityReserveId}/detail`
        }
        navigate(url, {state: {previousPageUrl: `/resident/condominiums/${condominiumCd}/my-reservations`}})
    }

    calculateCharges(infos: ReservationStatusCardInformation[]): { accountCharge: number, cashCharge: number } {
        let accountCharge: number = 0
        let cashCharge: number = 0
        infos.forEach(info => {
            if (info.reserveStatus !== RESERVATION_STATUS.ELECTION) {
                return
            }
            info.chargeInformations.forEach(chargeInfo => {
                const {charge, paymentMethod} = chargeInfo
                if (paymentMethod === PAYMENT_TYPE.CASH) {
                    cashCharge += charge
                } else {
                    accountCharge += charge
                }
            })
        })
        return {accountCharge, cashCharge}
    }

    isFutureReservation(info: ReservationStatusCardInformation): boolean {
        const {currentTime} = this.state
        if (currentTime === null) {
            return false
        }
        if (info instanceof ReservationCardInformation) {
            return currentTime.isSameOrBefore(info.reserveEndDatetime, 'day')
        } else {
            return currentTime.isSameOrBefore(info.useDate, 'day')
        }
    }

    makeScrollTargetName(facilityReserveId: number): string {
        return `ReservationStatusCard-${facilityReserveId}`
    }

    renderChargeSummary(accountCharge: number, cashCharge: number): ReactNode {
        return (<>
            <RowFlexContainer>
                <div>ご利用金額（合計）</div>
                <Charge>{formatNumberWithCommas(accountCharge + cashCharge)}円</Charge>
            </RowFlexContainer>
        </>)
    }

    renderChargeDetails(accountCharge: number, cashCharge: number): ReactNode {
        return (<>
            <BlockSpacer height={'22px'}/>
            <RowFlexContainer>
                <div>口座振替</div>
                <Charge>{formatNumberWithCommas(accountCharge)}円</Charge>
            </RowFlexContainer>
            <BlockSpacer height={'10px'}/>
            <RowFlexContainer>
                <div>現金</div>
                <Charge>{formatNumberWithCommas(cashCharge)}円</Charge>
            </RowFlexContainer>
        </>)
    }

    renderReservationStatusCards(
        displayCardInfos: ReservationStatusCardInformation[],
    ): ReactNode {
        if (displayCardInfos.length === 0) {
            return (<>
                <BlockSpacer height={'30px'}/>
                該当する予約はありません
            </>)
        }

        return (<CardList>
            {
                displayCardInfos.map((cardInfo, index) => {
                    return <CardWrapper key={index} name={this.makeScrollTargetName(cardInfo.facilityReserveId)}>
                        <ReservationCard
                            cardInformation={cardInfo}
                            onClick={this.handleClick.bind(this, cardInfo)}
                            highlight={(this.state.highlightFutureReservations && this.isFutureReservation(cardInfo))}
                        />
                    </CardWrapper>
                })
            }
        </CardList>)
    }

    render(): ReactNode {
        const {reservationCardInformations, searchDate, currentTime, showCanceledReservations} = this.state
        if (searchDate === null || currentTime === null) {
            return null
        }

        let prevMonthLink: ReactNode = (<PrevMonth onClick={this.handleClickPrevMonth}>前月</PrevMonth>)
        if (searchDate.isSameOrBefore(`${HKConstants.VMN.FAC.MIN_SELECTABLE_YEAR}-01`, 'month')) {
            prevMonthLink = (<BlockSpacer width={'50px'}/>)
        }

        let nextMonthLink: ReactNode = (<NextMonth onClick={this.handleClickNextMonth}>翌月</NextMonth>)
        const checkNextMonth = `${currentTime.year() + 1}-12`
        if (searchDate.isSameOrAfter(checkNextMonth, 'month')) {
            nextMonthLink = (<BlockSpacer width={'50px'}/>)
        }

        const {accountCharge, cashCharge} = this.calculateCharges(this.state.reservationCardInformations)

        const displayCardInfos = showCanceledReservations ? reservationCardInformations :
            reservationCardInformations.filter(
                (info) => (info.reserveStatus !== RESERVATION_STATUS.CANCEL) &&
                    (info.reserveStatus !== RESERVATION_STATUS.DIS_ELECTION),
            )

        this.scrollTargetName = null
        if (currentTime.isSame(searchDate, 'month')) {
            const moveTargetCardInfo = displayCardInfos.find(info => this.isFutureReservation(info))
            if (moveTargetCardInfo !== undefined) {
                this.scrollTargetName = this.makeScrollTargetName(moveTargetCardInfo.facilityReserveId)
            }
        }

        return <>
            <ReservationStatusesHeader>
                <ReservationStatusesHeaderContents>
                    <BlockSpacerPcOnly height={'17px'}/>
                    <MonthNavigation>
                        <RowFlexContainer>
                            {prevMonthLink}
                            <div>{searchDate.format('YYYY年M月')}</div>
                            {nextMonthLink}
                        </RowFlexContainer>
                    </MonthNavigation>
                </ReservationStatusesHeaderContents>
                <HideInPC>
                    <BlockSpacer height={'20px'}/>
                    <ReservationStatusesHeaderContents>
                        <MonthlyCharge>
                            <CollapseContainer
                                summary={this.renderChargeSummary(accountCharge, cashCharge)}
                                details={this.renderChargeDetails(accountCharge, cashCharge)}
                            />
                        </MonthlyCharge>
                    </ReservationStatusesHeaderContents>
                    <BlockSpacer height={'20px'}/>
                </HideInPC>
                <ReservationStatusesHeaderContents>
                    <LeftColumnFlexContainer>
                        {
                            this.scrollTargetName && <>
                                <PlainLinkStyledButton onClick={this.handleClickMoveToFutureReservation}>
                                    これからの予約を見る
                                </PlainLinkStyledButton>
                                <BlockSpacer height={'15px'}/>
                            </>
                        }
                        <label>
                            <ShowCanceledReservations>
                                <input type="checkbox"
                                       checked={this.state.showCanceledReservations}
                                       onChange={this.handleChangeShowCanceledReservations}
                                />
                                落選・キャンセルした予約を含める
                            </ShowCanceledReservations>
                        </label>
                    </LeftColumnFlexContainer>
                </ReservationStatusesHeaderContents>
                <HideInSP>
                    <ReservationStatusesHeaderContents>
                        <MonthlyCharge>
                            {this.renderChargeSummary(accountCharge, cashCharge)}
                            {this.renderChargeDetails(accountCharge, cashCharge)}
                        </MonthlyCharge>
                    </ReservationStatusesHeaderContents>
                </HideInSP>
            </ReservationStatusesHeader>
            {this.renderReservationStatusCards(displayCardInfos)}
            <BlockSpacer height={'15px'}/>
        </>
    }
}

export default RMRReservationStatusesComponent

const SingleLineText = styled.div`
  line-height: 1;
`

const ReservationStatusesHeader = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  align-content: space-between;
  ${currentMedia.lessThan('sp')`
    align-items: center;
  `}
  ${currentMedia.greaterThan('pc')`
    flex-wrap: wrap;
    height: 114px;
  `}
`

const ReservationStatusesHeaderContents = styled.div`
  ${currentMedia.lessThan('sp')`
    width: 100%;
  `}
  ${currentMedia.greaterThan('pc')`
    width: 354px;
    max-width: 354px;
  `}
`

const MonthNavigation = styled(SingleLineText)`
  margin: 0 auto;
  font-size: ${FontSize.Body};
  line-height: ${FontSize.Body};
`

const PrevMonth = styled(PlainLinkStyledButton)`
  position: relative;
  padding-left: 15px;
  text-align: left;
  margin: 0;
  width: 50px;

  :before {
    ${backArrow};
  }
`

const NextMonth = styled(PlainLinkStyledButton)`
  position: relative;
  padding-right: 15px;
  text-align: right;
  margin: 0;
  width: 50px;

  :after {
    ${forwardArrow};
  }
`

const ShowCanceledReservations = styled(LeftRowFlexContainer)`
  height: 16px;
  line-height: 16px;
`

const MonthlyCharge = styled.div`
  box-sizing: border-box;
  margin: 0 auto;
  padding: 17px 15px;

  background-color: ${Colors.Surface};
  line-height: ${FontSize.Body};
`

const Charge = styled.div`
  font-weight: ${FontWeights.Bold};
`

const CardWrapper = styled(Element)`
  ${CardWidth};
`
